import { useState, useEffect, useCallback, useRef } from 'react';
import { db } from '../../../firebaseConfig';
import { collection, query as firestoreQuery, orderBy, startAt, endAt, limit, getDocs } from 'firebase/firestore';

/**
 * Hook for searching users by username or display name
 * Handles Firestore search with fallback to client-side filtering
 * 
 * @param {string} searchQuery - The search query string
 * @param {Object} options - Configuration options
 * @param {number} options.limit - Maximum number of results (default: 15)
 * @param {string[]} options.excludeIds - Array of user IDs to exclude from results
 * @param {boolean} options.enabled - Whether to perform search (default: true when query.length >= 2)
 * @returns {Object} { results, isSearching, error }
 */
const useUserSearch = (searchQuery, options = {}) => {
    const {
        limit: maxResults = 15,
        excludeIds = [],
        enabled = true
    } = options;

    const [results, setResults] = useState([]);
    const [isSearching, setIsSearching] = useState(false);
    const [error, setError] = useState(null);

    // FIX #7: Stabilize excludeIds via ref to prevent useCallback recreation
    const excludeIdsRef = useRef(excludeIds);
    excludeIdsRef.current = excludeIds;

    // FIX #1: Renamed param from `query` → `searchText` to avoid shadowing Firestore `query`
    // FIX #12: Return cleanup function from performSearch to cancel in-flight fetches
    const performSearch = useCallback(async (searchText, signal) => {
        if (!searchText || searchText.trim().length < 2) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        const trimmed = searchText.trim().toLowerCase();
        if (!enabled || trimmed.length < 2) {
            setResults([]);
            setIsSearching(false);
            return;
        }

        setIsSearching(true);
        setError(null);

        const currentExcludeIds = excludeIdsRef.current;

        try {
            const usersRef = collection(db, 'users');
            let users = [];

            // Strategy 1: Search by username_lower (requires Firestore index)
            try {
                const q = firestoreQuery(
                    usersRef,
                    orderBy('username_lower'),
                    startAt(trimmed),
                    endAt(trimmed + '\uf8ff'),
                    limit(maxResults)
                );
                const snapshot = await getDocs(q);
                if (signal?.aborted) return;
                users = snapshot.docs
                    .map(doc => ({ id: doc.id, ...doc.data() }))
                    .filter(user => !currentExcludeIds.includes(user.id));
            } catch (indexError) {
                if (signal?.aborted) return;
                console.log('[useUserSearch] username_lower search failed, using fallback:', indexError.message);
            }

            // Strategy 2: Fallback - Get recent users and filter client-side
            if (users.length === 0) {
                try {
                    // FIX M-8: Reduced from 100 to 30 to limit cost; add index to avoid this path
                    console.warn('[useUserSearch] Using expensive fallback search \u2014 add username_lower index');
                    const fallbackQuery = firestoreQuery(
                        usersRef,
                        orderBy('__name__'),
                        limit(30)
                    );
                    const fallbackSnapshot = await getDocs(fallbackQuery);
                    if (signal?.aborted) return;
                    const recentUsers = fallbackSnapshot.docs
                        .map(doc => ({ id: doc.id, ...doc.data() }))
                        .filter(user => !currentExcludeIds.includes(user.id));

                    // Filter client-side by multiple fields
                    users = recentUsers.filter(user => {
                        const searchableFields = [
                            user.username?.toLowerCase(),
                            user.displayName?.toLowerCase(),
                            user.username_lower?.toLowerCase(),
                            user.name?.toLowerCase(),
                        ].filter(Boolean);

                        return searchableFields.some(field =>
                            field.includes(trimmed)
                        );
                    }).slice(0, maxResults);
                } catch (fallbackError) {
                    if (signal?.aborted) return;
                    console.error('[useUserSearch] Fallback search failed:', fallbackError);
                }
            }

            if (!signal?.aborted) {
                setResults(users);
            }
        } catch (err) {
            if (signal?.aborted) return;
            console.error('[useUserSearch] User search error:', err);
            setError(err);
            setResults([]);
        } finally {
            if (!signal?.aborted) {
                setIsSearching(false);
            }
        }
    }, [maxResults, enabled]); // FIX #7: excludeIds removed from deps, read from ref

    // Debounced search execution with proper cancellation
    useEffect(() => {
        const controller = new AbortController();

        const timer = setTimeout(() => {
            performSearch(searchQuery, controller.signal);
        }, 300); // 300ms debounce

        return () => {
            clearTimeout(timer);
            controller.abort(); // FIX #12: Cancel in-flight fetches on cleanup
        };
    }, [searchQuery, performSearch]);

    const reset = useCallback(() => {
        setResults([]);
        setIsSearching(false);
        setError(null);
    }, []);

    return {
        results,
        isSearching,
        error,
        reset,
        performSearch // Expose for manual triggering if needed
    };
};

export default useUserSearch;
